/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { BaseResponse } from '../models/base-response';
import { GetPaymentAdaptersResponse } from '../models/get-payment-adapters-response';
import { PaymentAdapterConfigHistoryResponse } from '../models/payment-adapter-config-history-response';
import { PaymentAdapterConfigRequest } from '../models/payment-adapter-config-request';
import { PaymentAdapterConfigResponse } from '../models/payment-adapter-config-response';
import { PaymentAdapterConfigTemplateHistoryResponse } from '../models/payment-adapter-config-template-history-response';
import { PaymentAdapterConfigTemplateRequest } from '../models/payment-adapter-config-template-request';
import { PaymentAdapterConfigTemplateResponse } from '../models/payment-adapter-config-template-response';

@Injectable({
  providedIn: 'root',
})
export class ApiService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation getPaymentAdapterConfigTemplatesByTenantCode
   */
  static readonly GetPaymentAdapterConfigTemplatesByTenantCodePath = '/v2/config/templates';

  /**
   * Get Supported Payment Adapters Templates By Tenant Code.
   *
   * This API is used to get the supported payment adapter templates for specified tenantCode
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPaymentAdapterConfigTemplatesByTenantCode()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPaymentAdapterConfigTemplatesByTenantCode$Response(params: {
    'tenant-code': string;
  }): Observable<StrictHttpResponse<GetPaymentAdaptersResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.GetPaymentAdapterConfigTemplatesByTenantCodePath, 'get');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<GetPaymentAdaptersResponse>;
      })
    );
  }

  /**
   * Get Supported Payment Adapters Templates By Tenant Code.
   *
   * This API is used to get the supported payment adapter templates for specified tenantCode
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPaymentAdapterConfigTemplatesByTenantCode$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPaymentAdapterConfigTemplatesByTenantCode(params: {
    'tenant-code': string;
  }): Observable<GetPaymentAdaptersResponse> {

    return this.getPaymentAdapterConfigTemplatesByTenantCode$Response(params).pipe(
      map((r: StrictHttpResponse<GetPaymentAdaptersResponse>) => r.body as GetPaymentAdaptersResponse)
    );
  }

  /**
   * Path part for operation createPaymentAdapterConfigTemplate
   */
  static readonly CreatePaymentAdapterConfigTemplatePath = '/v2/config/templates';

  /**
   * Create Payment Adapter Config Template.
   *
   * This API is used to create the payment adapter config template for a specified tenantCode
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createPaymentAdapterConfigTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createPaymentAdapterConfigTemplate$Response(params: {
    'tenant-code': string;

    /**
     * PaymentAdapterConfigTemplate object contains configuration information
     */
    body: PaymentAdapterConfigTemplateRequest
  }): Observable<StrictHttpResponse<PaymentAdapterConfigTemplateResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.CreatePaymentAdapterConfigTemplatePath, 'post');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigTemplateResponse>;
      })
    );
  }

  /**
   * Create Payment Adapter Config Template.
   *
   * This API is used to create the payment adapter config template for a specified tenantCode
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createPaymentAdapterConfigTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createPaymentAdapterConfigTemplate(params: {
    'tenant-code': string;

    /**
     * PaymentAdapterConfigTemplate object contains configuration information
     */
    body: PaymentAdapterConfigTemplateRequest
  }): Observable<PaymentAdapterConfigTemplateResponse> {

    return this.createPaymentAdapterConfigTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigTemplateResponse>) => r.body as PaymentAdapterConfigTemplateResponse)
    );
  }

  /**
   * Path part for operation createStorePaymentAdapterConfig
   */
  static readonly CreateStorePaymentAdapterConfigPath = '/v2/config/stores';

  /**
   * Create Store Payment Adapter Config.
   *
   * This API is used to create the store payment adapter config for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createStorePaymentAdapterConfig()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createStorePaymentAdapterConfig$Response(params: {
    'tenant-code': string;
    'store-number': string;

    /**
     * PaymentAdapterConfig object that contains information of order placed by user
     */
    body: PaymentAdapterConfigRequest
  }): Observable<StrictHttpResponse<PaymentAdapterConfigResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.CreateStorePaymentAdapterConfigPath, 'post');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.header('store-number', params['store-number'], {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigResponse>;
      })
    );
  }

  /**
   * Create Store Payment Adapter Config.
   *
   * This API is used to create the store payment adapter config for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createStorePaymentAdapterConfig$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createStorePaymentAdapterConfig(params: {
    'tenant-code': string;
    'store-number': string;

    /**
     * PaymentAdapterConfig object that contains information of order placed by user
     */
    body: PaymentAdapterConfigRequest
  }): Observable<PaymentAdapterConfigResponse> {

    return this.createStorePaymentAdapterConfig$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigResponse>) => r.body as PaymentAdapterConfigResponse)
    );
  }

  /**
   * Path part for operation createTenantPaymentAdapterConfig
   */
  static readonly CreateTenantPaymentAdapterConfigPath = '/v2/config';

  /**
   * Create Tenant Payment Adapter Config.
   *
   * This API is used to create the tenant payment adapter config for a specified tenantCode and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTenantPaymentAdapterConfig()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTenantPaymentAdapterConfig$Response(params: {
    'tenant-code': string;

    /**
     * PaymentAdapterConfig object contains configuration information for a specific adapter
     */
    body: PaymentAdapterConfigRequest
  }): Observable<StrictHttpResponse<PaymentAdapterConfigResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.CreateTenantPaymentAdapterConfigPath, 'post');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigResponse>;
      })
    );
  }

  /**
   * Create Tenant Payment Adapter Config.
   *
   * This API is used to create the tenant payment adapter config for a specified tenantCode and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createTenantPaymentAdapterConfig$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTenantPaymentAdapterConfig(params: {
    'tenant-code': string;

    /**
     * PaymentAdapterConfig object contains configuration information for a specific adapter
     */
    body: PaymentAdapterConfigRequest
  }): Observable<PaymentAdapterConfigResponse> {

    return this.createTenantPaymentAdapterConfig$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigResponse>) => r.body as PaymentAdapterConfigResponse)
    );
  }

  /**
   * Path part for operation getStorePaymentAdapterConfig
   */
  static readonly GetStorePaymentAdapterConfigPath = '/v2/config/stores/adapters/{adapter}';

  /**
   * Get Store Payment Adapter Config.
   *
   * This API is used to get the store payment adapter config for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getStorePaymentAdapterConfig()` instead.
   *
   * This method doesn't expect any request body.
   */
  getStorePaymentAdapterConfig$Response(params: {
    'tenant-code': string;
    'store-number': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<StrictHttpResponse<PaymentAdapterConfigResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.GetStorePaymentAdapterConfigPath, 'get');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.header('store-number', params['store-number'], {});
      rb.path('adapter', params.adapter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigResponse>;
      })
    );
  }

  /**
   * Get Store Payment Adapter Config.
   *
   * This API is used to get the store payment adapter config for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getStorePaymentAdapterConfig$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getStorePaymentAdapterConfig(params: {
    'tenant-code': string;
    'store-number': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<PaymentAdapterConfigResponse> {

    return this.getStorePaymentAdapterConfig$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigResponse>) => r.body as PaymentAdapterConfigResponse)
    );
  }

  /**
   * Path part for operation updateStorePaymentAdapterConfig
   */
  static readonly UpdateStorePaymentAdapterConfigPath = '/v2/config/stores/adapters/{adapter}';

  /**
   * Update Store Payment Adapter Config.
   *
   * This API is used to update the store payment adapter config for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateStorePaymentAdapterConfig()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateStorePaymentAdapterConfig$Response(params: {
    'tenant-code': string;
    'store-number': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT' | 'BAMBORA';
    body?: PaymentAdapterConfigRequest
  }): Observable<StrictHttpResponse<BaseResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.UpdateStorePaymentAdapterConfigPath, 'put');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.header('store-number', params['store-number'], {});
      rb.path('adapter', params.adapter, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BaseResponse>;
      })
    );
  }

  /**
   * Update Store Payment Adapter Config.
   *
   * This API is used to update the store payment adapter config for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateStorePaymentAdapterConfig$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateStorePaymentAdapterConfig(params: {
    'tenant-code': string;
    'store-number': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT' | 'BAMBORA';
    body?: PaymentAdapterConfigRequest
  }): Observable<BaseResponse> {

    return this.updateStorePaymentAdapterConfig$Response(params).pipe(
      map((r: StrictHttpResponse<BaseResponse>) => r.body as BaseResponse)
    );
  }

  /**
   * Path part for operation deleteStorePaymentAdapterConfig
   */
  static readonly DeleteStorePaymentAdapterConfigPath = '/v2/config/stores/adapters/{adapter}';

  /**
   * Delete Store Payment Adapter Config.
   *
   * This API is used to delete the store payment adapter config for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteStorePaymentAdapterConfig()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteStorePaymentAdapterConfig$Response(params: {
    'tenant-code': string;
    'store-number': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<StrictHttpResponse<BaseResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.DeleteStorePaymentAdapterConfigPath, 'delete');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.header('store-number', params['store-number'], {});
      rb.path('adapter', params.adapter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BaseResponse>;
      })
    );
  }

  /**
   * Delete Store Payment Adapter Config.
   *
   * This API is used to delete the store payment adapter config for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteStorePaymentAdapterConfig$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteStorePaymentAdapterConfig(params: {
    'tenant-code': string;
    'store-number': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<BaseResponse> {

    return this.deleteStorePaymentAdapterConfig$Response(params).pipe(
      map((r: StrictHttpResponse<BaseResponse>) => r.body as BaseResponse)
    );
  }

  /**
   * Path part for operation getTenantPaymentAdapterConfig
   */
  static readonly GetTenantPaymentAdapterConfigPath = '/v2/config/adapters/{adapter}';

  /**
   * Get Tanant Payment Adapter Config.
   *
   * This API is used to get the tenant payment adapter config for a specified tenantCode and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTenantPaymentAdapterConfig()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantPaymentAdapterConfig$Response(params: {
    'tenant-code': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<StrictHttpResponse<PaymentAdapterConfigResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.GetTenantPaymentAdapterConfigPath, 'get');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.path('adapter', params.adapter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigResponse>;
      })
    );
  }

  /**
   * Get Tanant Payment Adapter Config.
   *
   * This API is used to get the tenant payment adapter config for a specified tenantCode and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getTenantPaymentAdapterConfig$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantPaymentAdapterConfig(params: {
    'tenant-code': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<PaymentAdapterConfigResponse> {

    return this.getTenantPaymentAdapterConfig$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigResponse>) => r.body as PaymentAdapterConfigResponse)
    );
  }

  /**
   * Path part for operation updateTenantPaymentAdapterConfig
   */
  static readonly UpdateTenantPaymentAdapterConfigPath = '/v2/config/adapters/{adapter}';

  /**
   * Update Tenant Payment Adapter Config.
   *
   * This API is used to update the tenant payment adapter config for a specified tenantCode and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTenantPaymentAdapterConfig()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTenantPaymentAdapterConfig$Response(params: {
    'tenant-code': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
    body?: PaymentAdapterConfigRequest
  }): Observable<StrictHttpResponse<PaymentAdapterConfigResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.UpdateTenantPaymentAdapterConfigPath, 'put');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.path('adapter', params.adapter, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigResponse>;
      })
    );
  }

  /**
   * Update Tenant Payment Adapter Config.
   *
   * This API is used to update the tenant payment adapter config for a specified tenantCode and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateTenantPaymentAdapterConfig$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTenantPaymentAdapterConfig(params: {
    'tenant-code': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
    body?: PaymentAdapterConfigRequest
  }): Observable<PaymentAdapterConfigResponse> {

    return this.updateTenantPaymentAdapterConfig$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigResponse>) => r.body as PaymentAdapterConfigResponse)
    );
  }

  /**
   * Path part for operation deleteTenantPaymentAdapterConfig
   */
  static readonly DeleteTenantPaymentAdapterConfigPath = '/v2/config/adapters/{adapter}';

  /**
   * Delete Tenant Payment Adapter Config.
   *
   * This API is used to delete the tenant payment adapter config for a specified tenantCode and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteTenantPaymentAdapterConfig()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTenantPaymentAdapterConfig$Response(params: {
    'tenant-code': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<StrictHttpResponse<BaseResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.DeleteTenantPaymentAdapterConfigPath, 'delete');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.path('adapter', params.adapter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BaseResponse>;
      })
    );
  }

  /**
   * Delete Tenant Payment Adapter Config.
   *
   * This API is used to delete the tenant payment adapter config for a specified tenantCode and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteTenantPaymentAdapterConfig$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTenantPaymentAdapterConfig(params: {
    'tenant-code': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<BaseResponse> {

    return this.deleteTenantPaymentAdapterConfig$Response(params).pipe(
      map((r: StrictHttpResponse<BaseResponse>) => r.body as BaseResponse)
    );
  }

  /**
   * Path part for operation getTenantPaymentAdapterConfigTemplate
   */
  static readonly GetTenantPaymentAdapterConfigTemplatePath = '/v2/config/templates/{templateId}';

  /**
   * Get Payment Adapter Config Template.
   *
   * This API is used to get the payment adapter config template for a specified tenantCode and templateId
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTenantPaymentAdapterConfigTemplate()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantPaymentAdapterConfigTemplate$Response(params: {
    'tenant-code': string;
    templateId: string;
  }): Observable<StrictHttpResponse<PaymentAdapterConfigTemplateResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.GetTenantPaymentAdapterConfigTemplatePath, 'get');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.path('templateId', params.templateId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigTemplateResponse>;
      })
    );
  }

  /**
   * Get Payment Adapter Config Template.
   *
   * This API is used to get the payment adapter config template for a specified tenantCode and templateId
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getTenantPaymentAdapterConfigTemplate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantPaymentAdapterConfigTemplate(params: {
    'tenant-code': string;
    templateId: string;
  }): Observable<PaymentAdapterConfigTemplateResponse> {

    return this.getTenantPaymentAdapterConfigTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigTemplateResponse>) => r.body as PaymentAdapterConfigTemplateResponse)
    );
  }

  /**
   * Path part for operation updateTenantPaymentAdapterConfigTemplate
   */
  static readonly UpdateTenantPaymentAdapterConfigTemplatePath = '/v2/config/templates/{templateId}';

  /**
   * Update Payment Adapter Config Template.
   *
   * This API is used to update the payment adapter config template for a specified tenantCode and templateId
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTenantPaymentAdapterConfigTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTenantPaymentAdapterConfigTemplate$Response(params: {
    'tenant-code': string;
    templateId: string;
    body?: PaymentAdapterConfigTemplateRequest
  }): Observable<StrictHttpResponse<PaymentAdapterConfigTemplateResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.UpdateTenantPaymentAdapterConfigTemplatePath, 'put');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.path('templateId', params.templateId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigTemplateResponse>;
      })
    );
  }

  /**
   * Update Payment Adapter Config Template.
   *
   * This API is used to update the payment adapter config template for a specified tenantCode and templateId
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateTenantPaymentAdapterConfigTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTenantPaymentAdapterConfigTemplate(params: {
    'tenant-code': string;
    templateId: string;
    body?: PaymentAdapterConfigTemplateRequest
  }): Observable<PaymentAdapterConfigTemplateResponse> {

    return this.updateTenantPaymentAdapterConfigTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigTemplateResponse>) => r.body as PaymentAdapterConfigTemplateResponse)
    );
  }

  /**
   * Path part for operation deleteTenantPaymentAdapterConfigTemplate
   */
  static readonly DeleteTenantPaymentAdapterConfigTemplatePath = '/v2/config/templates/{templateId}';

  /**
   * Delete Payment Adapter Config Template.
   *
   * This API is used to delete the payment adapter config for a specified tenantCode and templateId
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteTenantPaymentAdapterConfigTemplate()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTenantPaymentAdapterConfigTemplate$Response(params: {
    'tenant-code': string;
    templateId: string;
  }): Observable<StrictHttpResponse<BaseResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.DeleteTenantPaymentAdapterConfigTemplatePath, 'delete');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.path('templateId', params.templateId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BaseResponse>;
      })
    );
  }

  /**
   * Delete Payment Adapter Config Template.
   *
   * This API is used to delete the payment adapter config for a specified tenantCode and templateId
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteTenantPaymentAdapterConfigTemplate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteTenantPaymentAdapterConfigTemplate(params: {
    'tenant-code': string;
    templateId: string;
  }): Observable<BaseResponse> {

    return this.deleteTenantPaymentAdapterConfigTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<BaseResponse>) => r.body as BaseResponse)
    );
  }

  /**
   * Path part for operation getPaymentAdaptersByTenantCode
   */
  static readonly GetPaymentAdaptersByTenantCodePath = '/v2/config/adapters';

  /**
   * Get Supported Tenant Payment Adapters.
   *
   * This API is used to get a list of supported payment adapters for specified tenantCode
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPaymentAdaptersByTenantCode()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPaymentAdaptersByTenantCode$Response(params: {
    'tenant-code': string;
  }): Observable<StrictHttpResponse<GetPaymentAdaptersResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.GetPaymentAdaptersByTenantCodePath, 'get');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<GetPaymentAdaptersResponse>;
      })
    );
  }

  /**
   * Get Supported Tenant Payment Adapters.
   *
   * This API is used to get a list of supported payment adapters for specified tenantCode
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPaymentAdaptersByTenantCode$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPaymentAdaptersByTenantCode(params: {
    'tenant-code': string;
  }): Observable<GetPaymentAdaptersResponse> {

    return this.getPaymentAdaptersByTenantCode$Response(params).pipe(
      map((r: StrictHttpResponse<GetPaymentAdaptersResponse>) => r.body as GetPaymentAdaptersResponse)
    );
  }

  /**
   * Path part for operation getPaymentAdaptersByTenantCodeAndStoreNumber
   */
  static readonly GetPaymentAdaptersByTenantCodeAndStoreNumberPath = '/v2/config/stores/adapters';

  /**
   * Get Supported Store Payment Adapters.
   *
   * This API is used to get a list of supported payment adapters for specified tenantCode and storeNumber
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPaymentAdaptersByTenantCodeAndStoreNumber()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPaymentAdaptersByTenantCodeAndStoreNumber$Response(params: {
    'tenant-code': string;
    'store-number': string;
  }): Observable<StrictHttpResponse<GetPaymentAdaptersResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.GetPaymentAdaptersByTenantCodeAndStoreNumberPath, 'get');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.header('store-number', params['store-number'], {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<GetPaymentAdaptersResponse>;
      })
    );
  }

  /**
   * Get Supported Store Payment Adapters.
   *
   * This API is used to get a list of supported payment adapters for specified tenantCode and storeNumber
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getPaymentAdaptersByTenantCodeAndStoreNumber$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPaymentAdaptersByTenantCodeAndStoreNumber(params: {
    'tenant-code': string;
    'store-number': string;
  }): Observable<GetPaymentAdaptersResponse> {

    return this.getPaymentAdaptersByTenantCodeAndStoreNumber$Response(params).pipe(
      map((r: StrictHttpResponse<GetPaymentAdaptersResponse>) => r.body as GetPaymentAdaptersResponse)
    );
  }

  /**
   * Path part for operation getStorePaymentAdapterConfigHistory
   */
  static readonly GetStorePaymentAdapterConfigHistoryPath = '/v2/config/history/stores/adapters/{adapter}';

  /**
   * Get Store Payment Adapter Config History.
   *
   * This API is used to fetch the store payment adapter config history for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getStorePaymentAdapterConfigHistory()` instead.
   *
   * This method doesn't expect any request body.
   */
  getStorePaymentAdapterConfigHistory$Response(params: {
    'tenant-code': string;
    'store-number': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<StrictHttpResponse<PaymentAdapterConfigHistoryResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.GetStorePaymentAdapterConfigHistoryPath, 'get');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.header('store-number', params['store-number'], {});
      rb.path('adapter', params.adapter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigHistoryResponse>;
      })
    );
  }

  /**
   * Get Store Payment Adapter Config History.
   *
   * This API is used to fetch the store payment adapter config history for a specified tenantCode, storeNumber and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getStorePaymentAdapterConfigHistory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getStorePaymentAdapterConfigHistory(params: {
    'tenant-code': string;
    'store-number': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<PaymentAdapterConfigHistoryResponse> {

    return this.getStorePaymentAdapterConfigHistory$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigHistoryResponse>) => r.body as PaymentAdapterConfigHistoryResponse)
    );
  }

  /**
   * Path part for operation getTenantPaymentAdapterConfigHistory
   */
  static readonly GetTenantPaymentAdapterConfigHistoryPath = '/v2/config/history/adapters/{adapter}';

  /**
   * Get Store Payment Adapter Config History.
   *
   * This API is used to fetch the tenant payment adapter config history for a specified tenantCode and adapter
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTenantPaymentAdapterConfigHistory()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantPaymentAdapterConfigHistory$Response(params: {
    'tenant-code': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<StrictHttpResponse<PaymentAdapterConfigHistoryResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.GetTenantPaymentAdapterConfigHistoryPath, 'get');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.path('adapter', params.adapter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigHistoryResponse>;
      })
    );
  }

  /**
   * Get Store Payment Adapter Config History.
   *
   * This API is used to fetch the tenant payment adapter config history for a specified tenantCode and adapter
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getTenantPaymentAdapterConfigHistory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantPaymentAdapterConfigHistory(params: {
    'tenant-code': string;
    adapter: 'FIRST_DATA_RAPID_CONNECT' | 'AUTHORIZE_NET' | 'PGW_AUTHORIZE_NET' | 'STRIPE' | 'VANTIV' | 'PAYMARK' | 'BPOINT';
  }): Observable<PaymentAdapterConfigHistoryResponse> {

    return this.getTenantPaymentAdapterConfigHistory$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigHistoryResponse>) => r.body as PaymentAdapterConfigHistoryResponse)
    );
  }

  /**
   * Path part for operation getTenantPaymentAdapterConfigTemplateHistory
   */
  static readonly GetTenantPaymentAdapterConfigTemplateHistoryPath = '/v2/config/history/templates/{templateId}';

  /**
   * Get Payment Adapter Config Template History.
   *
   * This API is used to fetch the payment adapter config template history for a specified tenantCode and templateId
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTenantPaymentAdapterConfigTemplateHistory()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantPaymentAdapterConfigTemplateHistory$Response(params: {
    'tenant-code': string;
    templateId: string;
  }): Observable<StrictHttpResponse<PaymentAdapterConfigTemplateHistoryResponse>> {

    const rb = new RequestBuilder(this.rootUrl, ApiService.GetTenantPaymentAdapterConfigTemplateHistoryPath, 'get');
    if (params) {
      rb.header('tenant-code', params['tenant-code'], {});
      rb.path('templateId', params.templateId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<PaymentAdapterConfigTemplateHistoryResponse>;
      })
    );
  }

  /**
   * Get Payment Adapter Config Template History.
   *
   * This API is used to fetch the payment adapter config template history for a specified tenantCode and templateId
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getTenantPaymentAdapterConfigTemplateHistory$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantPaymentAdapterConfigTemplateHistory(params: {
    'tenant-code': string;
    templateId: string;
  }): Observable<PaymentAdapterConfigTemplateHistoryResponse> {

    return this.getTenantPaymentAdapterConfigTemplateHistory$Response(params).pipe(
      map((r: StrictHttpResponse<PaymentAdapterConfigTemplateHistoryResponse>) => r.body as PaymentAdapterConfigTemplateHistoryResponse)
    );
  }

}
